/**
 * Help article loader. Each Markdown file in ./content is its own lazy chunk,
 * so nothing here pulls article text into the main bundle until help is used.
 */
import { parseFrontmatter } from './frontmatter'
import { isSectionId, ROLES, SECTION_IDS, type HelpRole, type SectionId } from './sections'
import { extractHeadings, markdownToPlainText, type Heading } from './text'

export interface ArticleMeta {
  slug: string
  title: string
  summary: string
  section: SectionId
  order: number
  audience: HelpRole[]
  related: string[]
}

export interface Article extends ArticleMeta {
  body: string
  headings: Heading[]
  /** Plain text of the body, including code, for search. */
  text: string
  /** Plain text without code, for result snippets. */
  prose: string
}

type Loader = () => Promise<string>

const modules = import.meta.glob<string>('./content/*.md', { query: '?raw', import: 'default' })

export function slugFromPath(path: string): string {
  return path.replace(/^.*\//, '').replace(/\.md$/, '')
}

// A typography fixture with every element type, served at /help/kitchen-sink in
// development only. It is not listed in the help center or search.
const devFixtures: Record<string, Loader> = import.meta.env.DEV
  ? import.meta.glob<string>('./fixtures/*.md', { query: '?raw', import: 'default' })
  : {}

const loaders = new Map<string, Loader>(
  Object.entries(modules).map(([path, load]) => [slugFromPath(path), load]),
)

/** Slugs of every article file, known without loading any content. */
export const ARTICLE_SLUGS: readonly string[] = [...loaders.keys()].sort()

for (const [path, load] of Object.entries(devFixtures)) {
  const slug = slugFromPath(path)
  if (!loaders.has(slug)) loaders.set(slug, load)
}

export function hasArticle(slug: string): boolean {
  return loaders.has(slug)
}

/** Parse and check one article file. Throws with every problem listed. */
export function parseArticle(slug: string, source: string): { article: Article; errors: string[] } {
  const { data, body, errors } = parseFrontmatter(source)
  const str = (k: string) => {
    const v = data[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
    errors.push(`missing or empty "${k}"`)
    return ''
  }
  const list = (k: string, required: boolean) => {
    const v = data[k]
    if (Array.isArray(v)) return v
    if (typeof v === 'string' && v.trim()) return [v.trim()]
    if (required || v !== undefined) errors.push(`"${k}" must be a list`)
    return []
  }
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) errors.push(`slug "${slug}" is not lowercase-kebab`)
  const title = str('title')
  const summary = str('summary')
  const section = data.section
  if (!isSectionId(section)) errors.push(`unknown section "${String(section)}"`)
  const order = data.order
  if (typeof order !== 'number') errors.push('"order" must be an integer')
  const audience = list('audience', true)
  for (const a of audience)
    if (!(ROLES as readonly string[]).includes(a)) errors.push(`unknown audience "${a}"`)
  if (audience.length === 0) errors.push('"audience" needs at least one role')
  const related = list('related', false)
  const article: Article = {
    slug,
    title,
    summary,
    section: isSectionId(section) ? section : 'reference',
    order: typeof order === 'number' ? order : 999,
    audience: audience.filter((a): a is HelpRole => (ROLES as readonly string[]).includes(a)),
    related,
    body,
    headings: extractHeadings(body),
    text: markdownToPlainText(body),
    prose: markdownToPlainText(body, { code: false }),
  }
  return { article, errors }
}

const cache = new Map<string, Promise<Article>>()

export class ArticleNotFoundError extends Error {
  constructor(slug: string) {
    super(`No help article named "${slug}"`)
    this.name = 'ArticleNotFoundError'
  }
}

export function loadArticle(slug: string): Promise<Article> {
  const hit = cache.get(slug)
  if (hit) return hit
  const load = loaders.get(slug)
  if (!load) return Promise.reject(new ArticleNotFoundError(slug))
  const p = load().then((src) => parseArticle(slug, src).article)
  p.catch(() => cache.delete(slug))
  cache.set(slug, p)
  return p
}

let all: Promise<Article[]> | null = null

/** Every article, sorted by section then order. Loaded once and cached. */
export function loadAllArticles(): Promise<Article[]> {
  all ??= Promise.all(ARTICLE_SLUGS.map(loadArticle))
    .then(sortArticles)
    .catch((e: unknown) => {
      all = null
      throw e
    })
  return all
}

export function sortArticles<T extends ArticleMeta>(list: T[]): T[] {
  return [...list].sort(
    (a, b) =>
      sectionIndex(a.section) - sectionIndex(b.section) ||
      a.order - b.order ||
      a.title.localeCompare(b.title),
  )
}

function sectionIndex(id: SectionId): number {
  return SECTION_IDS.indexOf(id)
}
